import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Boxes, Layers, Trash2, Edit3, X, CheckCircle2, ImagePlus, Search, Sparkles, Tag, Brush, Copy, DollarSign, Square as SquareIcon, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import RealizedSkuWizard from "@/components/RealizedSkuWizard";
import ShadingMapModal from "@/components/ShadingMapModal";
import CaratteristicheDialog from "@/components/CaratteristicheDialog";
import GalleryDialog from "@/components/GalleryDialog";
import { FileText, Images } from "lucide-react";

function cartesian(arrays) {
  return arrays.reduce((acc, curr) => acc.flatMap((a) => curr.map((c) => [...a, c])), [[]]);
}

export default function CasaMadreProducts() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [realized, setRealized] = useState([]);
  const [network, setNetwork] = useState(null);
  const [open, setOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardMadre, setWizardMadre] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editingRealized, setEditingRealized] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoriaFilter, setCategoriaFilter] = useState("");
  const [activeTab, setActiveTab] = useState("template");
  const [form, setForm] = useState({
    sku_madre: "", nome: "", categoria: "", descrizione_short: "", descrizione_long: "",
    prezzo_base: 0, prezzo_minimo_retail: 0, prezzo_consigliato_retail: 0,
    fulfillment_method: "triangulation", lead_time_gg: 30,
    immagini: [], variant_axes: [], custom_attributes: {},
  });

  const [surfaceEditorEnabled, setSurfaceEditorEnabled] = useState(false);

  // Sprint A2 — Multi-select + bulk actions
  const [selectedTemplateIds, setSelectedTemplateIds] = useState(new Set());
  const [selectedRealizedIds, setSelectedRealizedIds] = useState(new Set());
  const [bulkPriceModal, setBulkPriceModal] = useState(null); // {type: 'template'|'realized'}
  const [shadingModalForId, setShadingModalForId] = useState(null);
  const [caratteristicheFor, setCaratteristicheFor] = useState(null);
  const [galleryFor, setGalleryFor] = useState(null);

  const toggleSelTemplate = (id) => setSelectedTemplateIds((s) =>
    new Set(s.has(id) ? [...s].filter((x) => x !== id) : [...s, id])
  );
  const toggleSelRealized = (id) => setSelectedRealizedIds((s) =>
    new Set(s.has(id) ? [...s].filter((x) => x !== id) : [...s, id])
  );

  const clearAllSelections = () => {
    setSelectedTemplateIds(new Set());
    setSelectedRealizedIds(new Set());
  };

  const bulkDuplicate = async (type) => {
    const ids = type === "template" ? [...selectedTemplateIds] : [...selectedRealizedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Duplicare ${ids.length} ${type === "template" ? "prodotti" : "SKU realizzati"}?`)) return;
    const endpoint = type === "template" ? "/products" : "/realized-products";
    let ok = 0;
    for (const id of ids) {
      try { await api.post(`${endpoint}/${id}/duplicate`); ok += 1; }
      catch (e) { console.error("dup", e); }
    }
    toast.success(`Duplicati ${ok}/${ids.length}`);
    clearAllSelections();
    refresh();
  };

  const bulkDelete = async (type) => {
    const ids = type === "template" ? [...selectedTemplateIds] : [...selectedRealizedIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Eliminare definitivamente ${ids.length} ${type === "template" ? "prodotti" : "SKU realizzati"}?`)) return;
    const endpoint = type === "template" ? "/products/bulk-delete" : "/realized-products/bulk-delete";
    try {
      const r = await api.post(endpoint, { ids });
      toast.success(`Eliminati ${r.data.deleted}`);
      clearAllSelections();
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const bulkGenerateShading = async () => {
    const ids = [...selectedTemplateIds];
    if (ids.length === 0) return;
    if (!window.confirm(`Generare la shading map per ${ids.length} prodotti?\n(usa parametri default: blur 2.5 · cutoff 2)`)) return;
    try {
      const r = await api.post("/products/bulk-generate-shading-map", { ids, blur_radius: 2.5, contrast_cutoff: 2 });
      toast.success(`Shading: ${r.data.generated} generate · ${r.data.skipped_no_image} senza foto · ${r.data.failed} errori`);
      clearAllSelections();
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const refresh = async () => {
    const [p, r, se] = await Promise.all([
      api.get("/products"),
      api.get("/realized-products"),
      api.get("/surface-editor/enabled").catch(() => ({ data: { enabled: false } })),
    ]);
    setProducts(p.data);
    setRealized(r.data);
    setSurfaceEditorEnabled(!!se.data?.enabled);
  };

  useEffect(() => {
    refresh();
    api.get(`/networks/${user.network_id}`).then((r) => {
      setNetwork(r.data);
      const cats = r.data.vertical_config?.categories || [];
      if (cats.length) setForm((f) => ({ ...f, categoria: cats[0] }));
    });
  }, [user.network_id]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", form);
      toast.success("Prodotto creato");
      setOpen(false);
      setForm({ ...form, sku_madre: "", nome: "", descrizione_short: "", descrizione_long: "", immagini: [], variant_axes: [] });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    }
  };

  const categories = network?.vertical_config?.categories || [];
  const fulfillments = network?.vertical_config?.fulfillment_methods || ["triangulation"];
  const term = network?.vertical_config?.terminology || {};

  // Categorie deducibili dai dati attuali (per il dropdown filtro)
  const dataCategories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => p.categoria && set.add(p.categoria));
    realized.forEach((r) => r.categoria && set.add(r.categoria));
    return Array.from(set).sort();
  }, [products, realized]);

  const filteredTemplates = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    return products.filter((p) => {
      if (categoriaFilter && (p.categoria || "").toLowerCase() !== categoriaFilter) return false;
      if (!q) return true;
      return [p.sku_madre, p.nome, p.categoria, p.materiali, p.descrizione_short]
        .some((f) => (f || "").toString().toLowerCase().includes(q));
    });
  }, [products, searchTerm, categoriaFilter]);

  const filteredRealized = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    return realized.filter((r) => {
      if (categoriaFilter && (r.categoria || "").toLowerCase() !== categoriaFilter) return false;
      if (!q) return true;
      return [r.sku_realizzato, r.sku_madre, r.nome, r.categoria]
        .some((f) => (f || "").toString().toLowerCase().includes(q));
    });
  }, [realized, searchTerm, categoriaFilter]);

  const openWizardFor = (madre) => {
    setWizardMadre(madre);
    setWizardOpen(true);
  };

  return (
    <AdminLayout>
      <div className="p-8 lg:p-10" data-testid="cm-products-page">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline text-brand mb-2">{term.casa_madre_label || "Casa Madre"}</div>
            <h1 className="font-heading font-bold text-4xl tracking-tight">{t("cm_products")}</h1>
            <p className="text-sm text-muted-foreground mt-2">Crea prodotti con assi varianti (es. tessuti × piedi) e attiva solo le combinazioni che metti a disposizione.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button data-testid="cm-new-product-btn"
                className="text-sm px-4 h-10 inline-flex items-center gap-1.5 rounded-sm bg-brand hover:bg-brand-hover text-white transition-colors">
                <Plus className="w-4 h-4" /> {t("cm_new_product")}
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
              <DialogHeader><DialogTitle>{t("cm_new_product")}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-3 mt-3">
                <div className="grid grid-cols-2 gap-3">
                  <input data-testid="prod-sku" required placeholder="SKU madre" value={form.sku_madre}
                    onChange={(e) => setForm({ ...form, sku_madre: e.target.value })}
                    className="h-10 px-3 rounded-sm border border-border text-sm font-mono" />
                  <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                    <SelectTrigger data-testid="prod-categoria" className="h-10 rounded-sm"><SelectValue placeholder="Categoria" /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <input data-testid="prod-nome" required placeholder="Nome prodotto" value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  className="w-full h-10 px-3 rounded-sm border border-border text-sm" />
                <textarea data-testid="prod-desc" placeholder="Descrizione breve" value={form.descrizione_short}
                  onChange={(e) => setForm({ ...form, descrizione_short: e.target.value })}
                  className="w-full px-3 py-2 rounded-sm border border-border text-sm" rows={2} />
                <div className="grid grid-cols-4 gap-3">
                  <input data-testid="prod-prezzo-base" type="number" step="0.01" placeholder="Prezzo base" value={form.prezzo_base}
                    onChange={(e) => setForm({ ...form, prezzo_base: parseFloat(e.target.value) || 0 })}
                    className="h-10 px-3 rounded-sm border border-border text-sm" />
                  <input data-testid="prod-prezzo-min" type="number" placeholder="Min retail" value={form.prezzo_minimo_retail}
                    onChange={(e) => setForm({ ...form, prezzo_minimo_retail: parseFloat(e.target.value) || 0 })}
                    className="h-10 px-3 rounded-sm border border-border text-sm" />
                  <input data-testid="prod-prezzo-cons" type="number" placeholder="Consigliato" value={form.prezzo_consigliato_retail}
                    onChange={(e) => setForm({ ...form, prezzo_consigliato_retail: parseFloat(e.target.value) || 0 })}
                    className="h-10 px-3 rounded-sm border border-border text-sm" />
                  <input data-testid="prod-lead-time" type="number" placeholder="Lead gg" value={form.lead_time_gg}
                    onChange={(e) => setForm({ ...form, lead_time_gg: parseInt(e.target.value) || 0 })}
                    className="h-10 px-3 rounded-sm border border-border text-sm" />
                </div>
                <Select value={form.fulfillment_method} onValueChange={(v) => setForm({ ...form, fulfillment_method: v })}>
                  <SelectTrigger data-testid="prod-fulfillment" className="h-10 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>{fulfillments.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                </Select>
                <ImageUploader value="" onChange={(u) => u && setForm((f) => ({ ...f, immagini: [...f.immagini, u] }))} label="Aggiungi immagine prodotto" />
                {form.immagini.length > 0 && (
                  <div className="flex gap-2 flex-wrap">
                    {form.immagini.map((u, i) => (
                      <div key={i} className="relative w-16 h-16 rounded-sm overflow-hidden border border-border">
                        <img src={u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" className="w-full h-full object-cover" />
                        <button type="button" onClick={() => setForm({ ...form, immagini: form.immagini.filter((_, idx) => idx !== i) })} className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-foreground text-white text-xs">×</button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-muted-foreground">💡 Dopo la creazione, dalla card prodotto clicca <strong>Galleria foto</strong> per aggiungere altre foto e impostare la cover.</p>
                <button data-testid="prod-submit" type="submit"
                  className="w-full h-11 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-hover transition-colors">
                  Crea prodotto
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* SEARCH BAR + FILTERS + TABS */}
        <div className="bg-white border border-border rounded-sm p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-3 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                data-testid="cm-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca per SKU, nome, materiale, descrizione…"
                className="w-full h-10 pl-9 pr-3 rounded-sm border border-border text-sm"
              />
            </div>
            <Select value={categoriaFilter || "__all__"} onValueChange={(v) => setCategoriaFilter(v === "__all__" ? "" : v)}>
              <SelectTrigger className="h-10 w-full md:w-56 rounded-sm" data-testid="cm-filter-categoria">
                <SelectValue placeholder="Tutte le categorie" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutte le categorie</SelectItem>
                {dataCategories.map((c) => (
                  <SelectItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-xs text-muted-foreground">
            {activeTab === "template" ? `${filteredTemplates.length} di ${products.length} template` : `${filteredRealized.length} di ${realized.length} SKU realizzati`}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4">
          <TabsList data-testid="cm-products-tabs">
            <TabsTrigger value="template" data-testid="tab-template">📚 Template ({products.length})</TabsTrigger>
            <TabsTrigger value="realized" data-testid="tab-realized">✨ SKU Realizzati ({realized.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="template" className="mt-4">
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-4 text-xs text-amber-900">
              💡 <strong>Template</strong> = modelli base (es. importati da PDF). Per renderli vendibili in vetrina, clicca <strong>Nuovo SKU</strong> e configura modello + tessuto + piede + metallo + prezzo + stock.
            </div>
            {filteredTemplates.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-border rounded-sm">
                <Boxes className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-muted-foreground">{searchTerm || categoriaFilter ? "Nessun template trovato con questi filtri" : t("cm_no_products")}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden">
                {filteredTemplates.map((p) => {
                  const isSel = selectedTemplateIds.has(p.id);
                  return (
                  <div key={p.id} className={`bg-white p-4 relative ${isSel ? "ring-2 ring-brand" : ""}`} data-testid={`cm-product-card-${p.sku_madre}`}>
                    <button onClick={(e) => { e.stopPropagation(); toggleSelTemplate(p.id); }}
                      data-testid={`cm-tpl-chk-${p.sku_madre}`}
                      className={`absolute top-2 left-2 w-6 h-6 rounded-sm flex items-center justify-center z-10 border-2 ${isSel ? "bg-brand border-brand text-white" : "bg-white/90 border-border hover:border-brand"}`}>
                      {isSel ? <CheckSquare className="w-3.5 h-3.5" /> : <SquareIcon className="w-3.5 h-3.5" />}
                    </button>
                    <div className="aspect-square overflow-hidden bg-neutral-100 rounded-sm mb-3 relative">
                      {p.immagini?.[0] && <img src={p.immagini[0]} alt={p.nome} className="w-full h-full object-cover" />}
                      {p.immagini?.length > 1 && (
                        <span className="absolute bottom-1 right-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-foreground/80 text-white">+{p.immagini.length - 1}</span>
                      )}
                    </div>
                    <div className="overline text-muted-foreground mb-1">{p.categoria}</div>
                    <h3 className="font-heading font-bold text-base mb-1 leading-tight">{p.nome}</h3>
                    <div className="text-xs text-muted-foreground font-mono mb-3">{p.sku_madre}</div>
                    <div className="flex items-center justify-between text-sm pt-3 border-t border-border">
                      <span className="font-medium">€ {p.prezzo_base || p.prezzo_consigliato_retail || 0}</span>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800">Template</span>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5 mt-2">
                      <button data-testid={`cm-new-sku-${p.sku_madre}`} onClick={() => openWizardFor(p)}
                        className="h-8 inline-flex items-center justify-center gap-1.5 rounded-sm bg-brand text-white text-xs hover:bg-brand-hover">
                        <Sparkles className="w-3.5 h-3.5" /> Nuovo SKU
                      </button>
                      <button data-testid={`cm-edit-${p.sku_madre}`} onClick={() => setEditingProduct(p)}
                        className="h-8 inline-flex items-center justify-center gap-1.5 rounded-sm border border-border text-xs hover:bg-accent">
                        <Edit3 className="w-3.5 h-3.5" /> Modifica
                      </button>
                    </div>
                    <button data-testid={`cm-caratt-${p.sku_madre}`} onClick={() => setCaratteristicheFor(p)}
                      className="mt-1.5 h-8 w-full inline-flex items-center justify-center gap-1.5 rounded-sm border border-border text-xs hover:bg-accent" title="Scheda tecnica e caratteristiche prodotto in vetrina">
                      <FileText className="w-3.5 h-3.5" /> Scheda tecnica
                    </button>
                    <button data-testid={`cm-gallery-${p.sku_madre}`} onClick={() => setGalleryFor(p)}
                      className="mt-1.5 h-8 w-full inline-flex items-center justify-center gap-1.5 rounded-sm border border-border text-xs hover:bg-accent" title="Galleria foto + cover">
                      <Images className="w-3.5 h-3.5" /> Galleria foto {p.immagini?.length ? `(${p.immagini.length})` : ""}
                    </button>
                    {/* Toggle configuratore visivo (Surface Editor tool) */}
                    {surfaceEditorEnabled && (
                      <label className="mt-1.5 h-8 w-full inline-flex items-center justify-between gap-1.5 rounded-sm border border-border px-2 text-xs cursor-pointer hover:bg-accent"
                        data-testid={`cm-cfg-enabled-${p.sku_madre}`}
                        title="Attiva il tool Configuratore Visivo (Surface Editor) per questo prodotto">
                        <span className="inline-flex items-center gap-1.5">
                          <Brush className="w-3.5 h-3.5" /> Configuratore visivo
                        </span>
                        <input type="checkbox"
                          checked={p.configurator_enabled === true}
                          onChange={async (e) => {
                            const next = e.target.checked;
                            try {
                              await api.put(`/products/${p.id}`, { configurator_enabled: next });
                              refresh();
                            } catch (err) {
                              toast.error("Errore: " + (err.response?.data?.detail || err.message));
                            }
                          }}
                          className="h-4 w-4" />
                      </label>
                    )}
                    {surfaceEditorEnabled && p.configurator_enabled === true && (
                      <div className="grid grid-cols-2 gap-1.5 mt-1.5">
                        <button data-testid={`cm-surfaces-${p.sku_madre}`}
                          onClick={() => window.location.href = `/casa-madre/surface-editor/${p.id}`}
                          className="h-8 inline-flex items-center justify-center gap-1.5 rounded-sm bg-purple-600 text-white text-xs hover:bg-purple-700">
                          <Brush className="w-3.5 h-3.5" />
                          {p.has_surfaces ? "Superfici" : "Disegna"}
                        </button>
                        <button data-testid={`cm-shading-${p.sku_madre}`}
                          onClick={() => setShadingModalForId(p.id)}
                          title={p.shading_url ? "Rigenera shading map" : "Genera shading map (luci/ombre)"}
                          className={`h-8 inline-flex items-center justify-center gap-1.5 rounded-sm text-xs ${p.shading_url ? "bg-purple-100 text-purple-700 hover:bg-purple-200" : "border border-purple-300 text-purple-700 hover:bg-purple-50"}`}>
                          <Sparkles className="w-3.5 h-3.5" /> {p.shading_url ? "Shading ✓" : "Shading"}
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="realized" className="mt-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 mb-4 text-xs text-emerald-900">
              ✅ <strong>SKU Realizzati</strong> = prodotti configurati pronti per la vendita. Solo questi appaiono in vetrina.
            </div>
            {filteredRealized.length === 0 ? (
              <div className="text-center py-20 border border-dashed border-border rounded-sm">
                <Sparkles className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
                <p className="text-muted-foreground mb-2">{searchTerm || categoriaFilter ? "Nessuno SKU realizzato con questi filtri" : "Nessuno SKU realizzato ancora. Vai sui Template e clicca \"Nuovo SKU\"."}</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden">
                {filteredRealized.map((r) => {
                  const isSel = selectedRealizedIds.has(r.id);
                  return (
                  <div key={r.id} className={`bg-white p-4 relative ${isSel ? "ring-2 ring-brand" : ""}`} data-testid={`cm-realized-card-${r.sku_realizzato}`}>
                    <button onClick={(e) => { e.stopPropagation(); toggleSelRealized(r.id); }}
                      data-testid={`cm-real-chk-${r.sku_realizzato}`}
                      className={`absolute top-2 left-2 w-6 h-6 rounded-sm flex items-center justify-center z-10 border-2 ${isSel ? "bg-brand border-brand text-white" : "bg-white/90 border-border hover:border-brand"}`}>
                      {isSel ? <CheckSquare className="w-3.5 h-3.5" /> : <SquareIcon className="w-3.5 h-3.5" />}
                    </button>
                    <div className="aspect-square overflow-hidden bg-neutral-100 rounded-sm mb-3 relative">
                      {r.immagini?.[0] && <img src={r.immagini[0]} alt={r.nome} className="w-full h-full object-cover" />}
                      {r.immagini?.length > 1 && (
                        <span className="absolute bottom-1 right-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-foreground/80 text-white">+{r.immagini.length - 1}</span>
                      )}
                      {r.tags?.length > 0 && (
                        <div className="absolute top-2 right-2 flex flex-wrap gap-1 max-w-[60%]">
                          {r.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-foreground text-white">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="overline text-muted-foreground mb-1">{r.categoria}</div>
                    <h3 className="font-heading font-bold text-base mb-1 leading-tight">{r.nome}</h3>
                    <div className="text-[11px] text-muted-foreground font-mono mb-2 break-all">{r.sku_realizzato}</div>
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-border">
                      <span className="font-bold">€ {r.prezzo_realizzato}</span>
                      <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${r.lane === "pronta_consegna" ? "bg-emerald-100 text-emerald-900" : r.lane === "prenotabile_lungo" ? "bg-blue-100 text-blue-900" : "bg-amber-100 text-amber-900"}`}>
                        {r.lane === "pronta_consegna" ? `${r.stock} in stock` : r.lane === "prenotabile_lungo" ? "Su prenotazione" : `${r.stock} pz · 3-5gg`}
                      </span>
                    </div>
                    <button data-testid={`cm-realized-edit-${r.sku_realizzato}`} onClick={() => setEditingRealized(r)}
                      className="mt-2 w-full h-8 inline-flex items-center justify-center gap-1.5 rounded-sm border border-border text-xs hover:bg-accent">
                      <Edit3 className="w-3.5 h-3.5" /> Modifica / Badge / Stock
                    </button>
                  </div>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {editingProduct && (
          <ProductMatrixEditor product={editingProduct} onClose={() => { setEditingProduct(null); refresh(); }} />
        )}
        {editingRealized && (
          <RealizedSkuEditor item={editingRealized} onClose={() => { setEditingRealized(null); refresh(); }} />
        )}
        {wizardOpen && wizardMadre && (
          <RealizedSkuWizard
            madre={wizardMadre}
            open={wizardOpen}
            onClose={() => { setWizardOpen(false); setWizardMadre(null); }}
            onCreated={() => { setWizardOpen(false); setWizardMadre(null); refresh(); setActiveTab("realized"); }}
          />
        )}

        {/* Sticky bulk action bar */}
        {(() => {
          const currentSel = activeTab === "template" ? selectedTemplateIds : selectedRealizedIds;
          if (currentSel.size === 0) return null;
          return (
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-40 bg-foreground text-white rounded-sm shadow-2xl px-4 py-2.5 flex items-center gap-2" data-testid="cm-bulk-bar">
              <span className="text-xs font-medium pr-2 border-r border-white/20" data-testid="cm-bulk-count">{currentSel.size} selezionati</span>
              <button onClick={() => bulkDuplicate(activeTab)} data-testid="cm-bulk-duplicate"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs hover:bg-white/10">
                <Copy className="w-3.5 h-3.5" /> Duplica
              </button>
              <button onClick={() => setBulkPriceModal({ type: activeTab })} data-testid="cm-bulk-price"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs hover:bg-white/10">
                <DollarSign className="w-3.5 h-3.5" /> Cambia prezzo
              </button>
              {activeTab === "template" && (
                <button onClick={bulkGenerateShading} data-testid="cm-bulk-shading"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs hover:bg-purple-500">
                  <Sparkles className="w-3.5 h-3.5" /> Genera shading
                </button>
              )}
              <button onClick={() => bulkDelete(activeTab)} data-testid="cm-bulk-delete"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs hover:bg-red-600">
                <Trash2 className="w-3.5 h-3.5" /> Elimina
              </button>
              <button onClick={clearAllSelections} data-testid="cm-bulk-clear" className="ml-2 text-xs text-white/60 hover:text-white">
                Annulla
              </button>
            </div>
          );
        })()}

        {bulkPriceModal && (
          <BulkPriceModal
            type={bulkPriceModal.type}
            ids={[...(bulkPriceModal.type === "template" ? selectedTemplateIds : selectedRealizedIds)]}
            onClose={() => setBulkPriceModal(null)}
            onApplied={() => { setBulkPriceModal(null); clearAllSelections(); refresh(); }}
          />
        )}

        {shadingModalForId && (
          <ShadingMapModal productId={shadingModalForId}
            productName={products.find((t) => t.id === shadingModalForId)?.nome}
            onClose={() => setShadingModalForId(null)}
            onApplied={() => { setShadingModalForId(null); refresh(); }} />
        )}

        {caratteristicheFor && (
          <CaratteristicheDialog product={caratteristicheFor}
            onClose={(saved) => { setCaratteristicheFor(null); if (saved) refresh(); }} />
        )}

        {galleryFor && (
          <GalleryDialog product={galleryFor}
            onClose={(saved) => { setGalleryFor(null); if (saved) refresh(); }} />
        )}
      </div>
    </AdminLayout>
  );
}

// ───────────────── Bulk Price Modal ─────────────────
function BulkPriceModal({ type, ids, onClose, onApplied }) {
  const [mode, setMode] = useState("percent");
  const [value, setValue] = useState(10);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const endpoint = type === "template" ? "/products/bulk-price" : "/realized-products/bulk-price";
      const r = await api.patch(endpoint, { ids, mode, value: Number(value) || 0 });
      toast.success(`Aggiornati ${r.data.updated} prezzi`);
      onApplied();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="cm-bulk-price-modal">
      <div className="bg-white rounded-sm p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 className="text-lg font-bold mb-1">Modifica prezzo bulk</h3>
        <p className="text-xs text-muted-foreground mb-4">Applica a {ids.length} {type === "template" ? "prodotti" : "SKU realizzati"}</p>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Modalità</label>
            <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full h-10 border border-border rounded-sm px-2 text-sm" data-testid="cm-bulk-mode">
              <option value="percent">Variazione %</option>
              <option value="absolute">Variazione assoluta (€)</option>
              <option value="set">Imposta valore (€)</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">
              Valore {mode === "percent" ? "(% — usa numero negativo per ribasso)" : mode === "absolute" ? "(+/- €)" : "(€)"}
            </label>
            <input type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)}
              className="w-full h-10 border border-border rounded-sm px-2 text-sm" data-testid="cm-bulk-value" />
          </div>
          <div className="text-[11px] text-muted-foreground bg-amber-50 border border-amber-200 rounded-sm p-2">
            {mode === "percent" && `Esempi: 10 = +10% · -20 = sconto del 20%`}
            {mode === "absolute" && `Esempi: 5 = +5€ · -10 = -10€`}
            {mode === "set" && `Tutti i prodotti selezionati verranno impostati a questo prezzo`}
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 h-10 border border-border rounded-sm text-sm">Annulla</button>
          <button onClick={submit} disabled={saving}
            className="flex-1 h-10 bg-brand text-white rounded-sm text-sm font-medium disabled:opacity-50" data-testid="cm-bulk-price-apply">
            {saving ? "..." : "Applica"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────── Realized SKU Editor (stock/lane/tags/prezzo) ───────────
function RealizedSkuEditor({ item, onClose }) {
  const [stock, setStock] = useState(item.stock || 0);
  const [lane, setLane] = useState(item.lane || "ordinabile_breve");
  const [prezzo, setPrezzo] = useState(item.prezzo_realizzato || 0);
  const [tags, setTags] = useState(item.tags || []);
  const [visibile, setVisibile] = useState(item.visibile_in_vetrina !== false);
  const [saving, setSaving] = useState(false);

  const TAGS_PREDEFINED = [
    { value: "scontato", label: "🔥 Scontato" },
    { value: "ultimi_pezzi", label: "📦 Ultimi pezzi" },
    { value: "edizione_limitata", label: "✨ Edizione limitata" },
    { value: "nuovo", label: "🆕 Nuovo" },
    { value: "best_seller", label: "⭐ Best seller" },
  ];

  const toggleTag = (t) => setTags(tags.includes(t) ? tags.filter((x) => x !== t) : [...tags, t]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/realized-products/${item.id}`, {
        stock: Number(stock) || 0, lane, prezzo_realizzato: Number(prezzo) || 0,
        tags, visibile_in_vetrina: visibile,
      });
      toast.success("SKU aggiornato");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Eliminare definitivamente ${item.sku_realizzato}?`)) return;
    try {
      await api.delete(`/realized-products/${item.id}`);
      toast.success("Eliminato");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="realized-editor">
        <DialogHeader>
          <DialogTitle>
            <div className="text-xs font-mono text-muted-foreground">{item.sku_realizzato}</div>
            <div className="mt-1">{item.nome}</div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Prezzo €</label>
              <input type="number" step="0.01" value={prezzo} onChange={(e) => setPrezzo(e.target.value)}
                data-testid="realized-prezzo"
                className="mt-1 w-full h-10 px-3 rounded-sm border border-border text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-muted-foreground">Stock pz</label>
              <input type="number" value={stock} onChange={(e) => setStock(e.target.value)}
                data-testid="realized-stock"
                className="mt-1 w-full h-10 px-3 rounded-sm border border-border text-sm" />
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Disponibilità (lane)</label>
            <Select value={lane} onValueChange={setLane}>
              <SelectTrigger className="h-10 rounded-sm" data-testid="realized-lane"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pronta_consegna">🟢 Pronta consegna (in stock)</SelectItem>
                <SelectItem value="ordinabile_breve">🟡 Ordinabile 3-5 giorni</SelectItem>
                <SelectItem value="prenotabile_lungo">🔵 Su prenotazione 40-60 giorni</SelectItem>
                <SelectItem value="non_disponibile">⚪ Non disponibile</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">Badge / Etichette</label>
            <div className="flex flex-wrap gap-1.5" data-testid="realized-tags">
              {TAGS_PREDEFINED.map((t) => (
                <button key={t.value} type="button" onClick={() => toggleTag(t.value)}
                  className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${tags.includes(t.value) ? "bg-foreground text-white border-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={visibile} onChange={(e) => setVisibile(e.target.checked)} data-testid="realized-visibile" />
            Visibile in vetrina
          </label>
          <div className="flex items-center gap-2 pt-3 border-t border-border">
            <button onClick={remove} data-testid="realized-delete"
              className="h-10 px-3 rounded-sm border border-red-200 text-red-700 hover:bg-red-50 text-xs">
              Elimina
            </button>
            <button onClick={save} disabled={saving} data-testid="realized-save"
              className="ml-auto h-10 px-5 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-hover disabled:opacity-50">
              {saving ? "Salvataggio…" : "Salva modifiche"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────── Matrix editor ───────────
function ProductMatrixEditor({ product, onClose }) {
  const [full, setFull] = useState(null);
  const [axes, setAxes] = useState([]);
  const [prezzoBase, setPrezzoBase] = useState(0);
  const [selectedCombos, setSelectedCombos] = useState({}); // key -> {stock_master, stock_imp, prezzo_riv, sku_v}
  const [tab, setTab] = useState("axes");
  const [savingAxes, setSavingAxes] = useState(false);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    const r = await api.get(`/products/${product.id}/full`);
    setFull(r.data);
    setAxes(r.data.prodotto.variant_axes || []);
    setPrezzoBase(r.data.prodotto.prezzo_base || r.data.prodotto.prezzo_minimo_retail || 0);
  };

  useEffect(() => { load(); }, [product.id]);

  const saveAxes = async () => {
    setSavingAxes(true);
    try {
      await api.put(`/products/${product.id}/variant-axes`, { variant_axes: axes, prezzo_base: prezzoBase });
      toast.success("Assi salvati");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    } finally { setSavingAxes(false); }
  };

  const addAxis = () => setAxes([...axes, { key: `asse${axes.length + 1}`, label: "Nuovo asse", type: "swatch", required: true, options: [] }]);
  const updateAxis = (i, patch) => setAxes(axes.map((a, idx) => idx === i ? { ...a, ...patch } : a));
  const removeAxis = (i) => setAxes(axes.filter((_, idx) => idx !== i));
  const addOption = (i) => updateAxis(i, { options: [...axes[i].options, { value: `opt${axes[i].options.length + 1}`, label: "Nuova opzione", image_url: "", price_delta: 0 }] });
  const updateOption = (i, j, patch) => updateAxis(i, { options: axes[i].options.map((o, idx) => idx === j ? { ...o, ...patch } : o) });
  const removeOption = (i, j) => updateAxis(i, { options: axes[i].options.filter((_, idx) => idx !== j) });

  // Generate all possible cells (without committing)
  const allCombos = useMemo(() => {
    if (!axes.length || axes.some((a) => !a.options.length)) return [];
    const optArrays = axes.map((a) => a.options.map((o) => ({ axis: a.key, value: o.value, label: o.label, delta: o.price_delta || 0 })));
    return cartesian(optArrays).map((tup) => {
      const combo = Object.fromEntries(tup.map((x) => [x.axis, x.value]));
      const labels = tup.map((x) => x.label);
      const delta = tup.reduce((s, x) => s + (Number(x.delta) || 0), 0);
      return { combo, labels, delta, key: tup.map((x) => `${x.axis}:${x.value}`).join("|") };
    });
  }, [axes]);

  // Existing variants map by combo key
  const existingByKey = useMemo(() => {
    const m = {};
    (full?.varianti || []).forEach((v) => {
      const key = Object.entries(v.combination || {}).sort().map(([k, val]) => `${k}:${val}`).join("|");
      m[key] = v;
    });
    return m;
  }, [full]);

  const toggleCombo = (cellKey, defaults) => {
    setSelectedCombos((sc) => {
      const copy = { ...sc };
      if (copy[cellKey]) {
        delete copy[cellKey];
      } else {
        copy[cellKey] = {
          sku_v: `${product.sku_madre}-V${(Object.keys(sc).length + (full?.varianti?.length || 0) + 1).toString().padStart(3, "0")}`,
          stock_master: 0, stock_imp: 0,
          prezzo_riv: Math.round((prezzoBase + defaults.delta) * 0.65),
        };
      }
      return copy;
    });
  };

  const createSelected = async () => {
    setCreating(true);
    try {
      const combinations = Object.entries(selectedCombos).map(([key, cfg]) => {
        const cell = allCombos.find((c) => c.key === key);
        return {
          combination: cell.combo,
          sku_variante: cfg.sku_v,
          prezzo_madre_to_rivenditore: Number(cfg.prezzo_riv) || 0,
          stock_master: Number(cfg.stock_master) || 0,
          stock_in_importazione: Number(cfg.stock_imp) || 0,
          custom_attributes: { labels: cell.labels },
          immagini_specifiche: [],
        };
      });
      const r = await api.post(`/products/${product.id}/variants-batch`, { combinations });
      toast.success(`${r.data.created.length} varianti create, ${r.data.skipped.length} saltate`);
      setSelectedCombos({});
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setCreating(false); }
  };

  const deleteVariant = async (vid) => {
    if (!window.confirm("Eliminare la variante?")) return;
    await api.delete(`/variants/${vid}`);
    toast.success("Variante eliminata");
    await load();
  };

  if (!full) return null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-auto">
        <DialogHeader>
          <DialogTitle data-testid="matrix-editor-title">
            <span className="font-mono text-xs text-muted-foreground">{product.sku_madre}</span><br />
            {product.nome}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="mt-2">
          <TabsList>
            <TabsTrigger value="axes" data-testid="tab-axes">Assi varianti ({axes.length})</TabsTrigger>
            <TabsTrigger value="matrix" data-testid="tab-matrix">Matrice combinazioni ({allCombos.length})</TabsTrigger>
            <TabsTrigger value="active" data-testid="tab-active">Varianti attive ({full.varianti.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="axes" className="space-y-4">
            <div className="flex items-center gap-3 mb-2">
              <label className="text-sm">Prezzo base €</label>
              <input data-testid="prezzo-base-input" type="number" step="0.01" value={prezzoBase}
                onChange={(e) => setPrezzoBase(parseFloat(e.target.value) || 0)}
                className="h-9 w-32 px-3 rounded-sm border border-border text-sm" />
              <button data-testid="save-axes-btn" onClick={saveAxes} disabled={savingAxes}
                className="ml-auto h-9 px-4 rounded-sm bg-brand text-white text-sm disabled:opacity-50">
                {savingAxes ? "Salvataggio..." : "Salva assi e prezzo base"}
              </button>
            </div>
            {axes.map((axis, i) => (
              <div key={i} className="border border-border rounded-sm p-4" data-testid={`axis-${i}`}>
                <div className="flex items-center gap-3 mb-3">
                  <input value={axis.key} onChange={(e) => updateAxis(i, { key: e.target.value })}
                    className="h-9 w-32 px-3 rounded-sm border border-border text-sm font-mono" placeholder="key (es. tessuto)" />
                  <input value={axis.label} onChange={(e) => updateAxis(i, { label: e.target.value })}
                    className="h-9 flex-1 px-3 rounded-sm border border-border text-sm" placeholder="Label visibile (es. Tessuto)" />
                  <Select value={axis.type || "swatch"} onValueChange={(v) => updateAxis(i, { type: v })}>
                    <SelectTrigger className="h-9 w-32 rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="swatch">Swatch immagine</SelectItem>
                      <SelectItem value="select">Dropdown</SelectItem>
                      <SelectItem value="dimension">Dimensione</SelectItem>
                    </SelectContent>
                  </Select>
                  <button data-testid={`remove-axis-${i}`} onClick={() => removeAxis(i)} className="w-9 h-9 rounded-sm border border-border hover:bg-red-50 text-red-600">
                    <Trash2 className="w-4 h-4 mx-auto" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {axis.options.map((opt, j) => (
                    <div key={j} className="flex items-center gap-2 border border-border rounded-sm p-2" data-testid={`axis-${i}-opt-${j}`}>
                      <div className="w-12 h-12 rounded-sm overflow-hidden bg-neutral-100 flex-shrink-0">
                        {opt.image_url ? <img src={opt.image_url} alt="" className="w-full h-full object-cover" /> :
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground"><ImagePlus className="w-4 h-4" /></div>}
                      </div>
                      <div className="flex-1 grid grid-cols-2 gap-1">
                        <input value={opt.value} onChange={(e) => updateOption(i, j, { value: e.target.value })}
                          placeholder="value" className="h-8 px-2 rounded-sm border border-border text-xs font-mono" />
                        <input value={opt.label} onChange={(e) => updateOption(i, j, { label: e.target.value })}
                          placeholder="Label" className="h-8 px-2 rounded-sm border border-border text-xs" />
                        <input value={opt.image_url || ""} onChange={(e) => updateOption(i, j, { image_url: e.target.value })}
                          placeholder="URL immagine" className="h-8 px-2 rounded-sm border border-border text-xs col-span-2" />
                        <input type="number" step="0.01" value={opt.price_delta || 0} onChange={(e) => updateOption(i, j, { price_delta: parseFloat(e.target.value) || 0 })}
                          placeholder="Δ prezzo €" className="h-8 px-2 rounded-sm border border-border text-xs" />
                      </div>
                      <button onClick={() => removeOption(i, j)} className="text-red-500 hover:bg-red-50 w-6 h-6 rounded-sm">×</button>
                    </div>
                  ))}
                </div>
                <button data-testid={`add-opt-${i}`} onClick={() => addOption(i)}
                  className="mt-3 h-8 px-3 rounded-sm border border-dashed border-border text-xs hover:bg-accent inline-flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> Aggiungi opzione
                </button>
              </div>
            ))}
            <button data-testid="add-axis-btn" onClick={addAxis}
              className="w-full h-11 rounded-sm border-2 border-dashed border-border text-sm hover:bg-accent inline-flex items-center justify-center gap-2">
              <Plus className="w-4 h-4" /> Aggiungi asse (es. tessuto, piedi, finitura)
            </button>
          </TabsContent>

          <TabsContent value="matrix">
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm text-xs text-amber-900">
              Seleziona solo le combinazioni che la Casa Madre mette a disposizione. Totale possibili: <b>{allCombos.length}</b>. Selezionate: <b>{Object.keys(selectedCombos).length}</b>.
            </div>
            {allCombos.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground">Definisci almeno 1 asse con opzioni nella tab Assi varianti.</div>
            ) : (
              <div className="overflow-auto max-h-[55vh] border border-border rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 w-8"></th>
                      {axes.map((a) => <th key={a.key} className="text-left p-2">{a.label}</th>)}
                      <th className="text-right p-2">Prezzo finale</th>
                      <th className="text-left p-2">SKU variante</th>
                      <th className="text-right p-2">€/Riv.</th>
                      <th className="text-center p-2">Stock</th>
                      <th className="text-center p-2">Importaz.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allCombos.map((cell) => {
                      const exists = existingByKey[cell.key];
                      const checked = !!selectedCombos[cell.key];
                      const cfg = selectedCombos[cell.key] || {};
                      const finale = prezzoBase + cell.delta;
                      return (
                        <tr key={cell.key} className={`border-t border-border ${exists ? "bg-emerald-50" : ""}`} data-testid={`row-${cell.key}`}>
                          <td className="p-2">
                            {exists ? (
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            ) : (
                              <input type="checkbox" checked={checked} onChange={() => toggleCombo(cell.key, cell)} data-testid={`combo-check-${cell.key}`} />
                            )}
                          </td>
                          {axes.map((a) => {
                            const opt = a.options.find((o) => o.value === cell.combo[a.key]);
                            return (
                              <td key={a.key} className="p-2">
                                <div className="flex items-center gap-1.5">
                                  {opt?.image_url && <img src={opt.image_url} alt="" className="w-6 h-6 rounded-sm object-cover" />}
                                  <span>{opt?.label}</span>
                                </div>
                              </td>
                            );
                          })}
                          <td className="p-2 text-right font-medium">€ {finale.toFixed(2)}</td>
                          <td className="p-2 font-mono">
                            {exists ? exists.sku_variante : (
                              <input value={cfg.sku_v || ""} onChange={(e) => setSelectedCombos((sc) => ({ ...sc, [cell.key]: { ...sc[cell.key], sku_v: e.target.value } }))}
                                className="h-7 px-2 w-44 rounded-sm border border-border text-xs font-mono" disabled={!checked} />
                            )}
                          </td>
                          <td className="p-2 text-right">
                            {exists ? `€ ${exists.prezzo_madre_to_rivenditore}` : (
                              <input type="number" step="0.01" value={cfg.prezzo_riv || ""} onChange={(e) => setSelectedCombos((sc) => ({ ...sc, [cell.key]: { ...sc[cell.key], prezzo_riv: parseFloat(e.target.value) || 0 } }))}
                                className="h-7 w-20 px-2 rounded-sm border border-border text-xs text-right" disabled={!checked} />
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {exists ? (exists.stock?.qta_disponibile || 0) : (
                              <input type="number" value={cfg.stock_master || 0} onChange={(e) => setSelectedCombos((sc) => ({ ...sc, [cell.key]: { ...sc[cell.key], stock_master: parseInt(e.target.value) || 0 } }))}
                                className="h-7 w-16 px-2 rounded-sm border border-border text-xs text-center" disabled={!checked} />
                            )}
                          </td>
                          <td className="p-2 text-center">
                            {exists ? (exists.stock?.qta_in_importazione || 0) : (
                              <input type="number" value={cfg.stock_imp || 0} onChange={(e) => setSelectedCombos((sc) => ({ ...sc, [cell.key]: { ...sc[cell.key], stock_imp: parseInt(e.target.value) || 0 } }))}
                                className="h-7 w-16 px-2 rounded-sm border border-border text-xs text-center" disabled={!checked} />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {Object.keys(selectedCombos).length > 0 && (
              <button data-testid="create-batch-btn" onClick={createSelected} disabled={creating}
                className="mt-4 h-11 px-6 rounded-sm bg-brand text-white text-sm disabled:opacity-50 inline-flex items-center gap-2">
                <Layers className="w-4 h-4" /> Crea {Object.keys(selectedCombos).length} varianti selezionate
              </button>
            )}
          </TabsContent>

          <TabsContent value="active">
            {full.varianti.length === 0 ? (
              <div className="text-center py-10 text-sm text-muted-foreground">Nessuna variante creata. Usa la tab Matrice per crearle.</div>
            ) : (
              <div className="overflow-auto max-h-[60vh] border border-border rounded-sm">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="text-left p-2">SKU</th>
                      <th className="text-left p-2">Combinazione</th>
                      <th className="text-right p-2">€/Rivenditore</th>
                      <th className="text-center p-2">Stock</th>
                      <th className="text-center p-2">In importazione</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {full.varianti.map((v) => (
                      <tr key={v.id} className="border-t border-border" data-testid={`variant-row-${v.sku_variante}`}>
                        <td className="p-2 font-mono">{v.sku_variante}</td>
                        <td className="p-2">{Object.entries(v.combination || {}).map(([k, val]) => <span key={k} className="inline-block mr-1 px-1.5 py-0.5 rounded-sm bg-neutral-100">{k}={val}</span>)}</td>
                        <td className="p-2 text-right">€ {v.prezzo_madre_to_rivenditore}</td>
                        <td className="p-2 text-center">{v.stock?.qta_disponibile || 0}</td>
                        <td className="p-2 text-center">{v.stock?.qta_in_importazione || 0}</td>
                        <td className="p-2 text-right">
                          <button data-testid={`del-variant-${v.sku_variante}`} onClick={() => deleteVariant(v.id)} className="text-red-500 hover:bg-red-50 w-7 h-7 rounded-sm"><Trash2 className="w-3.5 h-3.5 mx-auto" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
